import { useState, ReactNode, createContext, FC, useEffect, useMemo } from 'react';
import { endOfISOWeek, format, getISOWeek, getISOWeekYear, startOfISOWeek, subWeeks } from 'date-fns';
import axios from 'axios';

import {
    CathletesState,
    CathleteResponse,
    ClanMembersResponse,
    ClanMembersState,
    ClansResponse,
    ClansState,
    ClansTournamentInfo,
    DataContextType,
    ClansStateData,
    ClansTournamentState,
    ClanTournamentStats,
    BarData,
    ClanData,
    Seasons,
} from './DataProvider.types';
import { getClanImage, getScoreboardPosition, parseCSV } from '../utils/helpers';

export const DataContext = createContext<DataContextType | null>(null);

export const DataProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [clans, setClans] = useState<ClansState>(null);
    const [clanTournamentStats, setClanTournamentStats] = useState<ClanTournamentStats | null>(null);
    const [selectedClan, setSelectedClan] = useState<ClansStateData | null>(null);
    const [clansTournamentInfo, setClansTournamentInfo] = useState<ClansTournamentState>(null);
    const [cathletes, setCathletes] = useState<CathletesState>(null);
    const [clanMembers, setClanMembers] = useState<ClanMembersState>(null);
    const [seasons, setSeasons] = useState<Seasons | undefined>(undefined);

    const getTournamentClanData = (date: Date, selectedClanName: string) => {
        return new Promise<ClanData>(async (resolve) => {
            const week = `${format(startOfISOWeek(date), 'dd.MM')}-${format(endOfISOWeek(date), 'dd.MM')}`;
            const defaultValue = {
                wins: 0,
                win_rate: 0,
                pvp: 0,
                week,
            };
            await axios
                .get<Array<ClansResponse>>(
                    `https://stats.walken.io/clans/clans_${getISOWeekYear(date)}${('0' + getISOWeek(date)).slice(
                        -2,
                    )}.json`,
                )
                .then((res) => {
                    const clanHasData = res.data.find((clan) => clan.clan === selectedClanName);
                    if (clanHasData) {
                        resolve({
                            wins: clanHasData.win,
                            win_rate: clanHasData.winrate,
                            pvp: clanHasData.wlkn,
                            week,
                        });
                    } else {
                        resolve(defaultValue);
                    }
                })
                .catch(() => resolve(defaultValue));
        });
    };

    useEffect(() => {
        if (selectedClan && clans) {
            const getTournamentStats = async () => {
                const localTimezoneOffset = new Date().getTimezoneOffset();
                const localTimezoneOffsetMs = localTimezoneOffset * 60 * 1000;
                const dateWithOffset = Date.now() + localTimezoneOffsetMs;
                const week2Date = subWeeks(dateWithOffset, 1);
                const week3Date = subWeeks(dateWithOffset, 2);
                const week4Date = subWeeks(dateWithOffset, 3);
                const week5Date = subWeeks(dateWithOffset, 4);
                const week6Date = subWeeks(dateWithOffset, 5);

                Promise.all([
                    new Promise<ClanData>((resolve) => {
                        const clanHasData = clans.data.find((clan) => clan.clan === selectedClan.clan);
                        const week = `${format(startOfISOWeek(dateWithOffset), 'dd.MM')}-${format(
                            endOfISOWeek(dateWithOffset),
                            'dd.MM',
                        )}`;
                        if (clanHasData) {
                            resolve({
                                wins: clanHasData.win,
                                win_rate: clanHasData.winrate,
                                pvp: clanHasData.wlkn,
                                week,
                            });
                        } else {
                            resolve({ wins: 0, win_rate: 0, pvp: 0, week });
                        }
                    }),
                    getTournamentClanData(week2Date, selectedClan.clan),
                    getTournamentClanData(week3Date, selectedClan.clan),
                    getTournamentClanData(week4Date, selectedClan.clan),
                    getTournamentClanData(week5Date, selectedClan.clan),
                    getTournamentClanData(week6Date, selectedClan.clan),
                ])
                    .then((allPromises) => {
                        let wins: BarData[] = [];
                        let win_rate: BarData[] = [];
                        let pvp: BarData[] = [];
                        allPromises.forEach((period) => {
                            wins.unshift({ value: period.wins, week: period.week });
                            win_rate.unshift({ value: period.win_rate, week: period.week });
                            pvp.unshift({ value: period.pvp, week: period.week });
                        });
                        setClanTournamentStats({
                            wins,
                            win_rate,
                            pvp,
                        });
                    })
                    .catch((err) => console.log('Failed to load Tournament Stats', err));
            };
            getTournamentStats();
        }
    }, [selectedClan, clans]);

    const top10Cathletes = useMemo(() => {
        console.log('cathletes',cathletes);
        // const top10 = cathletes?.data.sort((a, b) => b.wlkn - a.wlkn).slice(0, 10);
        // const top10WithImages = top10?.map((cathlete) => {
        //     return {
        //         ...cathlete,
        //         clanPicture: getClanImage(cathlete.clan, clans?.data),
        //     };
        // });

        // return top10WithImages;
        return []
    }, [cathletes, clans?.data]);

    const top10ClanCathletes = useMemo(() => {
        const top10Clan = cathletes?.data
            .filter((cathlete) => cathlete.clan === selectedClan?.clan)
            .sort((a, b) => b.wlkn - a.wlkn)
            .slice(0, 10);

        return top10Clan;
    }, [cathletes, selectedClan]);

    const getClans = async () => {
        axios
            .get<Array<ClansResponse>>('https://stats.walken.io/clans/clans.json')
            .then((res) => {
                const last_modified = new Date(res.headers['last-modified']);
                setClans({
                    last_modified: last_modified,
                    data: getScoreboardPosition(res.data),
                });
            })
            .catch((error) => {
                console.log('getClans error', error);
            });
    };

    const getClanMembers = async () => {
        axios
            .get<Array<ClanMembersResponse>>('https://stats.walken.io/clans/clan_members.json')
            .then((res) => {
                const last_modified = new Date(res.headers['last-modified']);
                setClanMembers({
                    last_modified: last_modified,
                    data: res.data,
                });
            })
            .catch((error) => {
                console.log('getClanMembers error', error);
            });
    };

    const getClanTournamentInfo = async () => {
        axios
            .get<Array<ClansTournamentInfo>>('https://stats.walken.io/clans/clans_tournament.json')
            .then((res) => {
                const last_modified = new Date(res.headers['last-modified']);
                setClansTournamentInfo({
                    last_modified: last_modified,
                    data: res.data,
                });
            })
            .catch((error) => {
                console.log('getClanTournamentInfo error', error);
            });
    };

    const getCathletes = async () => {
        axios
            .get<Array<CathleteResponse>>('https://stats.walken.io/clans/clan_cats.json')
            .then((res) => {
                // const last_modified = new Date(res.headers['last-modified']);
                console.log('reponse',res);
                console.log('typeof', typeof res.data);
                // console.log('res.data',res.data);
                console.log('JSON parse', JSON.parse(res.data as any));
                // setCathletes({
                //     last_modified: last_modified,
                //     data: res.data,
                // });
            })
            .catch((error) => {
                console.log('getCathletes error', error);
            });
    };

    const getSeasons = async () => {
        axios
            .get<string>('https://stats.walken.io/clans/year_seasons.csv')
            .then((res) => {
                const parsedSeasons = parseCSV(res.data);
                setSeasons(parsedSeasons);
            })
            .catch((error) => {
                console.log('getSeasons error', error);
            });
    };

    const localTimezoneOffset = new Date().getTimezoneOffset();
    const localTimezoneOffsetMs = localTimezoneOffset * 60 * 1000;
    const seasonNumber = getISOWeek(Date.now() + localTimezoneOffsetMs);
    const seasonValues = useMemo(() => {
        return seasons ? seasons[seasonNumber - 1] : null;
    }, [seasons, seasonNumber]);

    useEffect(() => {
        getClans();
        getClanMembers();
        getClanTournamentInfo();
        getCathletes();
        getSeasons();
    }, []);

    const value: DataContextType = {
        cathletes,
        clans,
        clanMembers,
        seasons: seasons || null,
        top10Cathletes,
        top10ClanCathletes,
        totalClans: clans?.data.length || null,
        totalMembers: clanMembers?.data.length || null,
        selectedClan,
        setSelectedClan,
        clansTournamentInfo,
        seasonName: seasonValues?.name,
        seasonColor: seasonValues?.color,
        seasonNumber,
        clanTournamentStats,
    };

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
